import { CommonModule } from '@angular/common';
import {
   Component,
   EventEmitter,
   Input,
   OnChanges,
   Output,
   SimpleChanges,
   ViewChild
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';

import { WorkflowFile } from '../../../domain/workflow-file';
import { DatePropertyTimezonePipe } from '../../../pipes/date-property-timezone.pipe';
import { PhobosTaskInstanceService } from '../../../services/phobos-task-instance.service';
import DataUtils from '../../../util/data-utils';

@Component({
   selector: 'app-report-row-supporting-files-modal',
   standalone: true,
   imports: [
      CommonModule,
      ButtonModule,
      DialogModule,
      FileUploadModule,
      DatePropertyTimezonePipe
   ],
   templateUrl: './report-row-supporting-files-modal.component.html',
   styleUrls: ['./report-row-supporting-files-modal.component.scss']
})
export class ReportRowSupportingFilesModalComponent implements OnChanges {
   @ViewChild('fileUpload')
   fileUpload?: FileUpload;

   @Input() visible = false;
   @Input() taskInstanceId: number | null = null;
   @Input() pmsDataId: number | null = null;
   @Input() rowId: string | null = null;

   @Output() visibleChange = new EventEmitter<boolean>();

   @Output() fileCountChanged = new EventEmitter<{
      taskInstanceId: number;
      pmsDataId: number;
      targetRowId: string;
      fileCount: number;
   }>();

   supportingFiles: WorkflowFile[] = [];

   isLoading = false;
   isUploading = false;

   loadError = '';
   uploadError = '';
   uploadSuccess = '';

   private downloadingFileIds = new Set<number>();

   constructor(private phobosTaskInstanceService: PhobosTaskInstanceService) {}

   ngOnChanges(changes: SimpleChanges): void {
      if (!this.visible) {
         return;
      }

      const contextChanged =
         changes['visible']?.currentValue === true ||
         !!changes['taskInstanceId'] ||
         !!changes['pmsDataId'] ||
         !!changes['rowId'];

      if (contextChanged) {
         this.loadSupportingFiles();
      }
   }

   get canUpload(): boolean {
      return (
         !this.isLoading &&
         !this.isUploading &&
         !!this.taskInstanceId &&
         !!this.pmsDataId &&
         !!this.rowId?.trim()
      );
   }

   onDialogVisibleChange(nextVisible: boolean): void {
      if (!nextVisible) {
         this.closeModal();
      }
   }

   closeModal(): void {
      if (this.isUploading) {
         return;
      }

      this.fileUpload?.clear();
      this.visibleChange.emit(false);
   }

   loadSupportingFiles(): void {
      if (!this.taskInstanceId || !this.pmsDataId || !this.rowId?.trim()) {
         this.supportingFiles = [];
         this.loadError = '';
         return;
      }

      const taskInstanceId = this.taskInstanceId;
      const pmsDataId = this.pmsDataId;
      const rowId = this.rowId.trim();
      const requestContext = this.getContextKey(taskInstanceId, pmsDataId, rowId);

      this.isLoading = true;
      this.loadError = '';
      this.uploadError = '';
      this.uploadSuccess = '';

      this.phobosTaskInstanceService
         .getPmsRowSupportingFiles(taskInstanceId, pmsDataId, rowId)
         .subscribe({
            next: (files) => {
               if (requestContext !== this.getCurrentContextKey()) {
                  return;
               }

               this.supportingFiles = files;
               this.isLoading = false;

               this.fileCountChanged.emit({
                  taskInstanceId,
                  pmsDataId,
                  targetRowId: rowId,
                  fileCount: files.length
               });
            },
            error: (error: HttpErrorResponse) => {
               if (requestContext !== this.getCurrentContextKey()) {
                  return;
               }

               console.error(error);
               this.supportingFiles = [];
               this.loadError = 'Unable to load supporting documents.';
               this.isLoading = false;
            }
         });
   }

   onUpload(event: { files?: File[] }): void {
      const file = event.files?.[0];

      if (
         !file ||
         !this.taskInstanceId ||
         !this.pmsDataId ||
         !this.rowId?.trim() ||
         this.isUploading
      ) {
         return;
      }

      const taskInstanceId = this.taskInstanceId;
      const pmsDataId = this.pmsDataId;
      const rowId = this.rowId.trim();
      const requestContext = this.getContextKey(taskInstanceId, pmsDataId, rowId);

      this.isUploading = true;
      this.uploadError = '';
      this.uploadSuccess = '';

      this.phobosTaskInstanceService
         .uploadPmsRowSupportingFile(taskInstanceId, pmsDataId, rowId, file)
         .subscribe({
            next: (files) => {
               if (requestContext !== this.getCurrentContextKey()) {
                  return;
               }

               this.supportingFiles = files;
               this.isUploading = false;

               this.fileCountChanged.emit({
                  taskInstanceId,
                  pmsDataId,
                  targetRowId: rowId,
                  fileCount: files.length
               });

               this.uploadSuccess = `${file.name} uploaded successfully.`;
               this.fileUpload?.clear();
            },
            error: (error: HttpErrorResponse) => {
               if (requestContext !== this.getCurrentContextKey()) {
                  return;
               }

               console.error(error);
               this.isUploading = false;
               this.uploadError = 'Unable to upload the supporting document.';
               this.fileUpload?.clear();
            }
         });
   }

   downloadFile(file: WorkflowFile): void {
      if (
         !file.id ||
         !this.taskInstanceId ||
         !this.pmsDataId ||
         !this.rowId?.trim() ||
         this.downloadingFileIds.has(file.id)
      ) {
         return;
      }

      const supportingFileId = file.id;

      this.downloadingFileIds.add(supportingFileId);

      this.phobosTaskInstanceService
         .getPmsRowSupportingFileSource(
            this.taskInstanceId,
            this.pmsDataId,
            this.rowId.trim(),
            supportingFileId
         )
         .subscribe({
            next: (sourceFile) => {
               this.downloadingFileIds.delete(supportingFileId);
               DataUtils.downloadBase64File(sourceFile);
            },
            error: (error: HttpErrorResponse) => {
               console.error(error);
               this.downloadingFileIds.delete(supportingFileId);
               this.loadError = 'Unable to download the supporting document.';
            }
         });
   }

   isDownloading(file: WorkflowFile): boolean {
      return !!file.id && this.downloadingFileIds.has(file.id);
   }

   trackFileById(index: number, file: WorkflowFile): number | string {
      return file.id ?? file.fileName ?? index;
   }

   private getCurrentContextKey(): string {
      if (!this.taskInstanceId || !this.pmsDataId || !this.rowId?.trim()) {
         return '';
      }

      return this.getContextKey(
         this.taskInstanceId,
         this.pmsDataId,
         this.rowId.trim()
      );
   }

   private getContextKey(
      taskInstanceId: number,
      pmsDataId: number,
      rowId: string
   ): string {
      return `${taskInstanceId}:${pmsDataId}:${rowId}`;
   }
}
